import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import {
  createQueryKey,
  FUNDRAISING_ADVISORY_BASE_KEY,
} from "@/api/query-keys.ts"
import {
  GetBusinessInfoOptionsResponse,
  GetBusinessInformationsResponse,
  GetSuggestedIndustryTagsRequest,
  GetSuggestedIndustryTagsResponse,
  GetSuggestedTagsRequest,
  GetSuggestedTagsResponse,
} from "@/feature/fundraising-advisory/business-information/api/types.ts"
import { axiosInstance } from "@/api/axios.ts"
import { selectCountryOptions } from "@/global/nationality.ts"

const TAGS = {
  businessInformation: `businessInformation`,
  businessInformationOptions: `businessInformationOptions`,
  suggestedTags: `suggestedTags`,
  suggestedIndustryTags: `suggestedIndustryTags`,
}

// ***** GET -> BUSINESS INFORMATION OPTIONS ***** //

export const getBusinessInformationOptions = async (params: {
  flatten: 0 | 1
}): Promise<GetBusinessInfoOptionsResponse> =>
  Object.assign(
    (
      await axiosInstance.get<GetBusinessInfoOptionsResponse>(
        `/industry-classification/all`,
        {
          params,
        }
      )
    ).data,
    {
      country: selectCountryOptions(`start`).map((country, i) => ({
        ...country,
        id: i + 1,
      })),
    }
  )

export const getBusinessInformationOptionsQuery = (params: {
  flatten: 0 | 1
}) => ({
  queryKey: createQueryKey({
    baseKey: FUNDRAISING_ADVISORY_BASE_KEY,
    keys: [TAGS.businessInformationOptions, `flatten_${params.flatten}`],
  }),
  queryFn: () => getBusinessInformationOptions(params),
})

export const useGetBusinessInformationOptions = (params?: { flatten: 0 | 1 }) =>
  useSuspenseQuery(getBusinessInformationOptionsQuery(params ?? { flatten: 0 }))

// ***** GET -> BUSINESS INFORMATIONS *****

const BUSINESS_INFORMATION_QUERY_KEY = createQueryKey({
  baseKey: FUNDRAISING_ADVISORY_BASE_KEY,
  keys: [TAGS.businessInformation],
})

export const getBusinessInformations =
  async (): Promise<GetBusinessInformationsResponse> =>
    (
      await axiosInstance.get<GetBusinessInformationsResponse>(
        `/industry-classification/`
      )
    ).data

export const getBusinessInformationsQuery = () => ({
  queryKey: BUSINESS_INFORMATION_QUERY_KEY,
  queryFn: getBusinessInformations,
})

export const useGetBusinessInformations = () =>
  useSuspenseQuery(getBusinessInformationsQuery())

//  **** PUT -> BUSINESS INFORMATIONS ****

const putBusinessInformations = async (
  request: GetBusinessInformationsResponse
): Promise<unknown> =>
  axiosInstance.post<
    GetBusinessInformationsResponse,
    GetBusinessInformationsResponse
  >(`/industry-classification/`, request)

export const useUpdateBusinessInformationsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: putBusinessInformations,
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: BUSINESS_INFORMATION_QUERY_KEY,
      })
    },
  })
}

// ***** GET -> TAGS ***** //
const getSuggestedTagsQueryKey = (options: GetSuggestedTagsRequest) =>
  createQueryKey({
    baseKey: FUNDRAISING_ADVISORY_BASE_KEY,
    keys: [
      TAGS.suggestedTags,
      (options !== undefined ? Object.values(options) : `noInput`) as string,
    ],
  })

export const getSuggestedTags = async (
  request: GetSuggestedTagsRequest
): Promise<GetSuggestedTagsResponse["tags"]> => {
  if (request === undefined) {
    return Promise.resolve([])
  }
  return (
    await axiosInstance.get<GetSuggestedTagsResponse>(
      `/industry-classification/generate/tags`,
      {
        params: {
          ...((request as { website: string }).website && {
            website: (request as { website: string }).website,
          }),
          ...((request as { description: string }).description && {
            description: (request as { description: string }).description,
          }),
        },
      }
    )
  ).data.tags
}

export const getSuggestedTagsQuery = (request: GetSuggestedTagsRequest) => ({
  queryKey: getSuggestedTagsQueryKey(request),
  queryFn: () => getSuggestedTags(request),
})

export const useGetSuggestedTags = (request: GetSuggestedTagsRequest) =>
  useSuspenseQuery(getSuggestedTagsQuery(request))

// ***** GET -> SUGGESTED INDUSTRY TAGS ***** //

export const getSuggestedIndustryTagsQueryKey = (
  options: GetSuggestedIndustryTagsRequest
) =>
  createQueryKey({
    baseKey: FUNDRAISING_ADVISORY_BASE_KEY,
    keys: [
      TAGS.suggestedIndustryTags,
      (options !== undefined ? Object.values(options) : `noInput`) as string,
    ],
  })

export const getSuggestedIndustryTags = async (
  request: GetSuggestedIndustryTagsRequest
): Promise<GetSuggestedIndustryTagsResponse> => {
  if (request === undefined) {
    return Promise.resolve({
      id: 0,
      code: ``,
      children: [],
    } as unknown as GetSuggestedIndustryTagsResponse)
  }
  return (
    await axiosInstance.get<GetSuggestedIndustryTagsResponse>(
      `/industry-classification/generate/industries`,
      {
        params: {
          flatten: 0,
          ...((request as { website: string }).website && {
            website: (request as { website: string }).website,
          }),
          ...((request as { description: string }).description && {
            description: (request as { description: string }).description,
          }),
        },
      }
    )
  ).data
}

export const getSuggestedIndustryTagsQuery = (
  request: GetSuggestedIndustryTagsRequest
) => ({
  queryKey: getSuggestedIndustryTagsQueryKey(request),
  queryFn: () => getSuggestedIndustryTags(request),
})

export const useGetSuggestedIndustryTags = (
  request: GetSuggestedIndustryTagsRequest
) => useSuspenseQuery(getSuggestedIndustryTagsQuery(request))
